import { smoothScrollToElement } from '../../utils/smoothScroll';
import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../ui/button';
import { ChevronLeft, ChevronRight, MapPin, Calendar, Zap, Home } from 'lucide-react';
import { workShowcaseItems } from '../../mock/mockData';

const WorkShowcaseSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sectionRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  // Auto slide functionality
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % workShowcaseItems.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % workShowcaseItems.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + workShowcaseItems.length) % workShowcaseItems.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const scrollToForm = () => {
    const formSection = document.getElementById('contact-form');
    if (formSection) {
      smoothScrollToElement('contact-form', 350);
    }
  };

  return (
    <section 
      ref={sectionRef} 
      className="relative py-20 overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(249,250,251,0.6) 0%, rgba(249,250,251,0.8) 50%, rgba(255,255,255,1) 100%)'
      }}
    >

      
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-40 right-20 w-80 h-80 rounded-full mix-blend-multiply filter blur-2xl animate-pulse"
             style={{backgroundColor: '#91d33d'}}></div>
        <div className="absolute bottom-40 left-20 w-80 h-80 rounded-full mix-blend-multiply filter blur-2xl animate-pulse"
             style={{backgroundColor: '#57c558', animationDelay: '3s'}}></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Unsere Projekt-
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-green-dark to-brand-green">
                Erfolgsgeschichten
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Über 2.000 zufriedene Kunden vertrauen bereits auf unsere Expertise. 
              Sehen Sie selbst, wie wir Träume von effizienter Heiztechnik verwirklichen.
            </p>
          </div>

          {/* Carousel */}
          <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Main Slide Container */}
            <div className="overflow-hidden rounded-3xl shadow-2xl bg-white">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {workShowcaseItems.map((item, index) => (
                  <div key={index} className="w-full flex-shrink-0">
                    <div className="grid lg:grid-cols-2 gap-0">
                      {/* Image */}
                      <div className="relative h-64 lg:h-96 bg-gradient-to-br from-gray-100 to-gray-200">

                        
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center space-y-4">
                            <div className="w-16 h-16 bg-gradient-to-r from-brand-green to-brand-green-hover rounded-full flex items-center justify-center mx-auto">
                              <Home className="w-8 h-8 text-white" />
                            </div>
                            <p className="text-gray-600 font-semibold">{item.title}</p>
                            <p className="text-sm text-gray-500">Projektbild Platzhalter</p>
                          </div>
                        </div>
                        
                        {/* Project Badge */}
                        <div className="absolute top-4 right-4 bg-brand-green text-white px-3 py-1 rounded-full text-sm font-semibold">
                          {item.category}
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="p-8 lg:p-12 flex flex-col justify-center">
                        <div className="space-y-6">
                          <div>
                            <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                              {item.title}
                            </h3>
                            <div className="flex items-center space-x-2 text-gray-500 mb-4">
                              <MapPin className="w-4 h-4" />
                              <span>{item.location}</span>
                              <span>•</span>
                              <Calendar className="w-4 h-4" />
                              <span>{item.year}</span>
                            </div>
                          </div>
                          
                          <p className="text-gray-600 leading-relaxed">
                            {item.description}
                          </p>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-gray-50 rounded-lg p-4">
                              <div className="flex items-center space-x-2 mb-2">
                                <Zap className="w-4 h-4 text-brand-green" />
                                <span className="text-sm text-gray-600">Leistung</span>
                              </div>
                              <div className="text-lg font-bold text-gray-900">{item.specs.power}</div>
                            </div>
                            
                            <div className="bg-gray-50 rounded-lg p-4">
                              <div className="flex items-center space-x-2 mb-2">
                                <Home className="w-4 h-4 text-brand-green" />
                                <span className="text-sm text-gray-600">Gebäudeart</span>
                              </div>
                              <div className="text-lg font-bold text-gray-900">{item.specs.buildingType}</div>
                            </div>
                          </div>
                          
                          <div className="bg-brand-green-light/10 rounded-lg p-4">
                            <p className="text-brand-green-dark font-semibold">
                              ✓ {item.result}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-colors z-10"
            >
              <ChevronLeft className="w-6 h-6 text-gray-600" />
            </button>
            
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-colors z-10"
            >
              <ChevronRight className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          {/* Dots Navigation */}
          <div className={`flex justify-center space-x-2 mt-8 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {workShowcaseItems.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentSlide ? 'bg-brand-green' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          {/* CTA */}
          <div className={`text-center mt-12 transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <Button
              onClick={scrollToForm}
              size="lg"
              className="bg-brand-orange hover:bg-brand-orange-hover text-white px-10 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              Kostenloses Angebot anfordern
            </Button>
            <p className="text-gray-500 mt-4 text-sm">
              Werden Sie Teil unserer Erfolgsgeschichten
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkShowcaseSection;